<script lang="ts">
    import tabIcon from "@/assets/gear.svg";

    export default {
        name: "TypAvGenomlysningsapparatList",
        tabName: "TypAvGenomlysningsapparat",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import CollapsibleSection from "@/components/generic/CollapsibleSection.vue";
    import { ref, onBeforeUnmount, onBeforeMount, computed } from "vue";
    import { openDialog } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import KButton from "@/components/Button.vue";
    import TypAvGenomlysningsapparatForm from "@/tabs/Operation/Admin/Genomlysningsapparat/TypAvGenomlysningsapparatForm.vue";
    import store from "@/store";

    // TODO Ändra till användning av type i typAvGenomlysningsapparatModule i Vue3
    interface TypAvGenomlysningsapparat {
        id: number;
        namn: string;
        kod: string;
        ordning: number;
    }

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);
    const filterParams = ref<
        | {
              [key: string]: string;
          }
        | undefined
    >();

    const subsetName = ref<string>("default");
    const typerAvGenomlysningsapparater = computed<TypAvGenomlysningsapparat[]>(() =>
        store.getters["typAvGenomlysningsapparat/typAvGenomlysningsapparatSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );

    const nyaTyperAvGenomlysningsapparater = computed<TypAvGenomlysningsapparat[]>(() =>
        store.getters["typAvGenomlysningsapparat/typAvGenomlysningsapparatSubset"]({
            subsetName: "newlyCreated",
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );
    const redigeradeTyperAvGenomlysningsapparater = computed<TypAvGenomlysningsapparat[]>(() =>
        store.getters["typAvGenomlysningsapparat/typAvGenomlysningsapparatSubset"]({
            subsetName: "newlyUpdated",
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );

    const count = ref(0);
    const columns = ref([
        {
            title: "Namn",
            name: "namn",
        },
        {
            title: "Kod",
            name: "kod",
        },
        {
            title: "Ordning",
            name: "ordning",
        },
    ]);

    const dropdownActions = ref([
        {
            name: "editTypAvGenomlysningsapparat",
            title: "Redigera typ av genomlysningsapparat",
            show: () => true,
        },
    ]);

    const limit = ref(50);
    const offset = ref(0);
    const fetchBatch = ref(1);
    const currentPage = ref(1);

    const loadPage = async (page: any) => {
        currentPage.value = page;
        offset.value = (page - 1) * limit.value;
        await fetchTyperAvGenomlysningsapparater({ page: listPage.value });
    };

    const loadMore = async () => {
        fetchBatch.value++;
        await fetchTyperAvGenomlysningsapparater({ page: listPage.value });
    };

    const changeLimit = async (dropdownLimit: any) => {
        fetchBatch.value = 1;
        limit.value = dropdownLimit;
        await fetchTyperAvGenomlysningsapparater({ page: listPage.value });
    };

    const fetchTyperAvGenomlysningsapparater = async ({
        page,
        limit = listLimit.value,
    }: {
        page: number;
        limit?: number;
    }) => {
        try {
            const {
                nextPage,
                total,
                subsetName: fetchedSubsetName,
            } = await store.dispatch("typAvGenomlysningsapparat/fetchTyperAvGenomlysningsapparater", {
                page,
                limit,
                filterParams: filterParams.value,
            });

            listPage.value = page;
            subsetName.value = fetchedSubsetName;

            if (total) {
                listCount.value = total;
            }
        } catch (_) {
            console.log(_);
            openDialog("Kunde inte hämta typer av genomlysningsapparater", "error");
        }
    };

    const openCreateTypAvGenomlysningsapparat = () => {
        store.dispatch("tabs/openTab", {
            component: TypAvGenomlysningsapparatForm,
        });
    };

    const editTypAvGenomlysningsapparatFromTable = (typAvGenomlysningsapparat: TypAvGenomlysningsapparat) => {
        store.dispatch("tabs/openTab", {
            component: TypAvGenomlysningsapparatForm,
            parameters: {
                typAvGenomlysningsapparatId: typAvGenomlysningsapparat.id,
            },
        });
    };

    onBeforeMount(async () => {
        await fetchTyperAvGenomlysningsapparater({ page: listPage.value });
        await store.dispatch("typAvGenomlysningsapparat/fetchTyperAvGenomlysningsapparater", {});
        await store.dispatch("typAvGenomlysningsapparat/addSubscriber");
    });

    onBeforeUnmount(async () => {
        await store.dispatch("typAvGenomlysningsapparat/removeSubscriber");
    });
</script>

<template>
    <div class="typ-av-genomlysningsapparat-list">
        <AdminLockBorder />
        <div class="header">
            <h1>Typer av genomlysningsapparater</h1>
            <KButton variant="hollow" @click="openCreateTypAvGenomlysningsapparat" :style="{ padding: 0 }">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Skapa typ av genomlysningsapparat</span>
            </KButton>
        </div>

        <div class="newly" v-if="redigeradeTyperAvGenomlysningsapparater.length > 0">
            <CollapsibleSection isSingle :isExpanded="true" heading="Senast redigerade">
                <KlinikenTable
                    :items="redigeradeTyperAvGenomlysningsapparater"
                    :columns="columns"
                    @editTypAvGenomlysningsapparat="editTypAvGenomlysningsapparatFromTable"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </CollapsibleSection>
        </div>
        <div class="newly-created" v-if="nyaTyperAvGenomlysningsapparater.length > 0">
            <CollapsibleSection isSingle :isExpanded="true" heading="Senast tillagda">
                <KlinikenTable
                    :items="nyaTyperAvGenomlysningsapparater"
                    :columns="columns"
                    @editTypAvGenomlysningsapparat="editTypAvGenomlysningsapparatFromTable"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </CollapsibleSection>
        </div>

        <div>
            <h3
                v-if="nyaTyperAvGenomlysningsapparater.length > 0 || redigeradeTyperAvGenomlysningsapparater.length > 0"
            >
                Alla
            </h3>
            <KlinikenTable
                v-if="typerAvGenomlysningsapparater"
                :items="typerAvGenomlysningsapparater"
                paginationStyle="paging"
                :columns="columns"
                :count="count"
                :limit="limit"
                :currentPage="currentPage"
                @loadPage="loadPage"
                @changeLimit="changeLimit"
                @loadMore="loadMore"
                :showBottom="true"
                :showLimitDropdown="true"
                :has-dropdown="true"
                :dropdown-actions="dropdownActions"
                @editTypAvGenomlysningsapparat="editTypAvGenomlysningsapparatFromTable"
            />
        </div>
    </div>
</template>

<style lang="scss">
    .typ-av-genomlysningsapparat-list {
        .header {
            h1 {
                margin: 0;
            }

            display: flex;
            gap: 20px;
            align-items: center;
            flex-wrap: wrap;
        }
    }
</style>
